import type { ChipProps } from '@heroui/react';

/**
 * How an invoice landed in our system.
 *
 *   - `excel`   → uploaded as a spreadsheet via the header's
 *                 "Excel import" CTA.
 *   - `invoice` → received electronically and accepted from the
 *                 `PendingInvoices` widget's "Import" CTA.
 *   - `manual`  → keyed in by hand via the header's "Manual import"
 *                 CTA.
 *
 * Kept as a string union (not an enum) to match the camelCase wire
 * convention the rest of the API uses.
 */
export type ImportMethod = 'excel' | 'invoice' | 'manual';

/**
 * One row in the Import History table — an imported invoice plus
 * `method` (how it was brought in) and `branchName` (where it landed).
 * No CTA column — history is read-only.
 */
export type ImportHistoryEntry = {
    branchName: string;
    /** The branch has been deleted. Its name still resolves (bos-api BOS-122). */
    branchDeleted?: boolean;
    /** The branch is archived — out of the pickers, still named here. */
    branchArchived?: boolean;
    currency: string;
    date: string;
    /** Ancillary charges on the invoice — freight, customs, … */
    expensesTotal: number;
    expensesTotalTetri: number;
    id: string;
    importedAt: string;
    invoiceNumber: string;
    /** The journal entry the invoice posted when it was received. */
    journalEntryId: string | null;
    /** The line items as issued. */
    linesTotal: number;
    linesTotalTetri: number;
    method: ImportMethod;
    /** Supplier payments allocated to this invoice (BOS-120). */
    paidAmount: number;
    paidAmountTetri: number;
    productCount: number;
    supplier: string;
    /**
     * The supplier has been deleted. The invoice is still history and the name
     * still resolves (bos-api BOS-122).
     */
    supplierDeleted?: boolean;
    /** The supplier is archived — out of the pickers, still named here. */
    supplierArchived?: boolean;
    /** `linesTotal + expensesTotal` — what the stock cost. */
    totalAmount: number;
    totalAmountTetri: number;
    /** VAT the supplier charged on the lines (bos-api BOS-125). */
    vatTotal: number;
    vatTotalTetri: number;
    /** `linesTotal + vatTotal` — the goods as billed. */
    grossTotal: number;
    grossTotalTetri: number;
    /** The part of `expensesTotal` the supplier billed on this invoice. */
    expensesOnInvoiceTotal: number;
    expensesOnInvoiceTotalTetri: number;
    /** `grossTotal + expensesOnInvoiceTotal` — what the supplier is owed. */
    payableTotal: number;
    payableTotalTetri: number;
};

/**
 * How much of an invoice is still owed: what the supplier is owed for it minus
 * what has been allocated to it. Clamped at zero — allocating more than an
 * invoice is worth leaves a prepayment credit at the *supplier* level, not a
 * negative outstanding on one document.
 *
 * Keys off `payableTotal`, not `totalAmount`: the landed total is what the
 * stock cost, which excludes the VAT the supplier charged and includes freight
 * owed to somebody else (bos-api BOS-125).
 */
export const outstandingAmount = (row: {
    paidAmountTetri: number;
    payableTotalTetri: number;
}): number => Math.max(row.payableTotalTetri - row.paidAmountTetri, 0);

/** Whether the invoice has been settled by the payments allocated to it. */
export const isSettled = (row: {
    paidAmountTetri: number;
    payableTotalTetri: number;
}): boolean => outstandingAmount(row) === 0;

/** Display formatter for `invoiceNumber` — kept local so the widget
 *  remains self-contained (no cross-widget imports). */
export const formatInvoiceNumber = (invoiceNumber: string): string =>
    `#${invoiceNumber}`;

/**
 * Iconify identifier rendered inside each method's Chip. Excel uses the
 * vscode icon for the green spreadsheet glyph (same one as the page-
 * header "Excel import" button) so users recognise the same affordance
 * across the page.
 */
export const IMPORT_METHOD_ICON: Record<ImportMethod, string> = {
    excel: 'vscode-icons:file-type-excel',
    invoice: 'solar:document-text-bold-duotone',
    manual: 'solar:pen-bold-duotone',
};

/**
 * Semantic Chip colour per method. Excel = success (green to echo the
 * spreadsheet glyph), invoice = accent (matches the page's brand-blue
 * "import" flow), manual = default (neutral — hand-entered data has no
 * inherent positive/negative connotation).
 */
export const IMPORT_METHOD_CHIP_COLOR: Record<
    ImportMethod,
    NonNullable<ChipProps['color']>
> = {
    excel: 'success',
    invoice: 'accent',
    manual: 'default',
};

/**
 * i18n key (relative to the `pages` namespace) for each method's label.
 */
export const IMPORT_METHOD_LABEL_KEY: Record<ImportMethod, string> = {
    excel: 'purchases.widgets.importHistory.methods.excel',
    invoice: 'purchases.widgets.importHistory.methods.invoice',
    manual: 'purchases.widgets.importHistory.methods.manual',
};

/**
 * Prefix bos-api stamps on the invoice it writes for stock entered through the
 * product form rather than through an import (BOS-118). Those receipts are
 * real, but no supplier ever issued the document — so the row is badged, and
 * nobody goes looking for the paper.
 */
const AUTO_INVOICE_PREFIX = 'AUTO-';

/** Whether this row's invoice was written by the system, not by a supplier. */
export const isAutoInvoice = (invoiceNumber: string): boolean =>
    invoiceNumber.startsWith(AUTO_INVOICE_PREFIX);

/** What the single Method chip on a row renders as. */
export type ImportMethodChipDisplay = {
    color: NonNullable<ChipProps['color']>;
    /** Iconify id, or `null` when the chip is text alone. */
    icon: string | null;
    labelKey: string;
};

/**
 * The one chip a row shows for how its invoice arrived (BOS-161).
 *
 * A system-written receipt used to render its import method *and* an `Auto`
 * badge — "Manual · Auto" side by side, which answers the same question twice
 * and disagrees with itself. `Auto` wins outright: nobody chose a method for a
 * receipt bos-api wrote itself when stock was entered through the product form,
 * so naming one is a claim about a decision that never happened. It carries no
 * icon because it is not one of the three import affordances on the page.
 */
export const importMethodChip = (
    method: ImportMethod,
    isAuto: boolean,
): ImportMethodChipDisplay =>
    isAuto
        ? {
              color: 'default',
              icon: null,
              labelKey: 'purchases.widgets.importHistory.auto',
          }
        : {
              color: IMPORT_METHOD_CHIP_COLOR[method],
              icon: IMPORT_METHOD_ICON[method],
              labelKey: IMPORT_METHOD_LABEL_KEY[method],
          };
